<template>
  <tr>
    <popup
        v-if="showDeletePrompt"
        @close="() => {showDeletePrompt = false;}"
    >
      <delete-prompt
          slot="popupMainContent"
          @abort="() => {showDeletePrompt = false;}"
          @confirm="() => {showDeletePrompt = false; deleteDataRowFinally();}"
      />
    </popup>
    <popup
        v-if="showEditForm"
        @close="() => {showEditForm = false;}"
    >
      <div slot="popupMainContent">
        <task-manager-edit-form
            :remote-row="row"
            @saveTask="saveTask"
        />
      </div>
    </popup>
    <td style="min-width: 520px">
      <div class="small lighter ml-1 mt-1">{{ row.key }}</div>
      <p class="bg-bright p-1 mb-1 ml-1 mb-2 border-radius mt-1">
        {{ $store.getters.getOrganizationDisplayName(organizationId) }}</p>
      <Button
          class="float-right"
          type="icon-button"
          @click="row.data.userId = $store.getters.getCurrentUserId; showEditForm = true">
        <icon type="gear"/>
      </Button>

      <h2 class="ml-1">{{ itemName ? itemName : '&nbsp;' }}</h2>
      <div :style="{backgroundColor: getColor(row.data.prio) || 'grey'}"
           class="p-1 mt-3 mb-1 ml-1 border-radius">
        <span class="lighter">Task:</span> {{ TaskManagerTaskTypes[row.data.task] ? TaskManagerTaskTypes[row.data.task] : row.data.task }}<br />
        <span class="lighter">State:</span> {{ getState(row.data.prio) }}
      </div>
      <div v-if="error" class="form-error">{{ error }}</div>


      <div v-if="row.data" class="ml-1 mb-1 form-section pb-1 pt-1">
        <span class="lighter taskfield">currentProcessor:</span>
        {{ row.data.currentProcessor ? row.data.currentProcessor : "N/A" }}<br/>
        <span class="lighter taskfield">currentScript:</span> {{
          row.data.currentScript ? row.data.currentScript : "N/A"
        }}<br/>
        <span class="lighter taskfield">scriptProgress:</span>
        {{ row.data.currentScriptProgress ? row.data.currentScriptProgress : "N/A" }}<br/>
        <span class="lighter taskfield">lastUpdate:</span>
        {{
          row.data && row.data.heartBeat ? dateFromISO(row.data.heartBeat) + ' ' + timeFromISO(row.data.heartBeat) : "never"
        }}<br/>
        <span class="lighter taskfield">Virtual Machine:</span>
        <vm-selector :display-only="true" :pre-set-value="row.data.virtualMachine"/>
        <br/>
      </div>
      <div class="form-section ml-1 pt-1 pb-2">
        <div v-if="waitingForResume">
          You just resumed the process – let's see if it resumes
        </div>
        <div
            v-else-if="((isOverDue(row.data && row.data.heartBeat ? row.data.heartBeat : null) || forceResume) && !resuming)"
            class="bg-highlight p-2 mt-2 mb-2">
          <div v-if="forceResume && !isOverDue(row.data.heartBeat)">You hit the forceResume-Button to show this panel
            here – make ABSOLUTELY SURE the task isn't running anymore, hit the reload button a few times, see it
            updates, wait at least for 1-2 minutes before doing anything
          </div>
          The last update for this process was
          <b>{{ row.data.heartBeat ? timeDifferenceFromTwoISOs(Date.now(), row.data.heartBeat).string : "N/A" }} ago</b><br/><br/>
          <div v-if="isOverDue(row.data.heartBeat)">It seems like this process is stuck</div>
          <br/>
          <div v-if="row.data.prio == 666">ERROR: There was an error, you can resume, but it might be the case you'll
            just repeat the same error again, depending on what the exact error is (proxy errors are usually okay)
          </div>
          <br/>
          <div v-if="['vhubPostProcessorConfig', 'importHierarchy'].includes(row.data.currentProcessor)"
               class="settings-button p-1 mt-2 mb-2 d-inline-block"
               @click="resume">Resume
          </div>
          <div v-else>If you're sure, extraction is stuck, open up "dangerous buttons" and hit the startTask button to
            restart the task
          </div>
        </div>
        <div v-else>
          No notifications at the moment
        </div>
      </div>
      <div class="form-section pt-2 pb-2">
        <div v-if="row.data && row.data.status" class="ml-1"><span class="lighter">Last status:</span>
          {{ row.data.status }}
        </div>

      </div>
      <div class="settings-button d-inline-block action-button float-none mt-1 mr-1" @click="setData">
        {{ $t('reload status') }}
        <icon type="rotate-right"/>
      </div>
      <router-link v-if="row.key" :to="'/library/3d-data/assembly/' + assetId"
                   class="settings-button d-inline-block mr-1" target="_blank">
        <icon type="arrow-up-right-from-square"/>
        Go to resource
      </router-link>
      <div class="settings-button d-inline-block mt-1 float-right" @click="deleteTask">
        {{ $t('delete task') }}
        <icon type="trash"/>
      </div>
      <info-panel
          v-if="$store.getters.isSuperAdmin"
          :collapsible="true"
          :info-panel-header="$t('Dangerous buttons')"
          :initially-collapsed="true"
          class="mt-4"
          preview-id="none"
      >
        <div slot="info">
          <div v-if="row.key" class="settings-button d-inline-block" @click="startTask">{{ $t('start Task') }}
            <icon type="angle-right"/>
          </div>
          <br/>
          <div v-if="row.key" class="settings-button d-inline-block mt-1" @click="finishTask">
            {{ $t('start PostProcessing') }}
            <icon type="box"/>
          </div>
        </div>
      </info-panel>
      <div class="ml-1 mt-3">
        <span class="lighter">See log files on VM.<br/>This will only work if the VM is running</span><br/>
        <div v-if="!fileLoading" class="settings-button d-inline-block" @click="listFiles">{{ $t('Load') }}</div>
        <loading-spinner v-if="fileLoading" class="mt-2"/>
        <select v-if="files.length" v-model="selectedFile" class="custom-select dark d-block mt-2">
          <option v-for="file in files" :key="file" :value="file">{{ file }}</option>
        </select>
        <div v-if="selectedFile" class="settings-button d-inline-block mt-2 mb-2" @click="loadFile">{{
            $t('loadFile')
          }}
        </div>
        <codemirror v-if="fileContent" :value="fileContent"/>
      </div>
      <div class="mt-5 force-resume clickable" @click="forceResume = !forceResume">.</div>
    </td>
    <td v-if="deletable" class="data-row-delete">
      <!--<Button slot="buttonsRight" type="icon-button"
      >
        <icon type="trash-alt"/>
      </Button>-->
    </td>
    <td v-else class="data-row-delete">
      <!--empty placeholder because we have the Special Column left-->
    </td>
    <td v-for="(field, property, columnIndex) in fields" v-if="!hiddenFields.includes(columnIndex)" :key="property">
      <transition mode="out-in" name="toggle">
        <div v-if="type === 'tbl' ? columnIndex === currentlyEditingRow.index : property === currentlyEditingRow.index"
             :key="'edit'"
             class=" ml-3">
          <date-picker-widget
              v-if="field.type === 'timestamp'"
              :value="currentlyEditingRow.value"
              class="mb-2"
              type="datetime"
              @save="updateDataRow"
          />
          <input
              v-else
              :id="dataRow.id"
              :ref="'entry'"
              v-model="currentlyEditingRow.value"
              class="form-text mb-2"
              type="text"
              @init="focusDataField"
          >
          <div class="edit-trigger mr-2" @click="finishEditingDataRow">
            <icon :type="'times'"/>
          </div>
          <div class="edit-trigger" @click="saveDataRow">
            <icon :type="'save'"/>
          </div>
        </div>
      </transition>
      <div v-if="columnIndex !== currentlyEditingRow.index" :key="'content'"
           class="table-filler"
           @click="editDataRow(columnIndex)">
        <div v-if="field.type === 'timestamp' && dataRow.data[getIndexType === 'columnIndex' ? columnIndex : property]"
             class="clickable withHoverBrightBackground pt-2 pb-2 pl-3 pr-4">
          {{ dateTimeFromISO(dataRow.data[columnIndex]) }}
          <icon :type="'edit'"/>
        </div>
        <div v-else-if="dataRow.data[columnIndex]"
             class="clickable withHoverBrightBackground pt-2 pb-2 pl-3 pr-4">
          {{ dataRow.data[columnIndex] }}
          <icon :type="'edit'"/>
        </div>
        <div v-else class="clickable withHoverBrightBackground pt-2 pb-2 pl-3 pr-4">
          <span class="lighter">{{ $t('addData') }}</span>
          <icon :type="'plus'"/>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import DatePickerWidget from "../editors/DatePickerWidget";
import DeletePrompt from "../../forms/DeletePrompt";
import Popup from "../../Popup";
import Icon from "../../Icon";
import DateMixin from '@/components/mixins/DateMixin.js';
import Button from "../../forms/Button";
import {SpecialUuids, TaskManagerStates, TaskManagerTaskColors, TaskManagerTaskTypes} from "@/enum";
import InfoPanel from "@/components/InfoPanel";
import vmSelector from "@/components/vmSelector";
import Codemirror from "../editors/Codemirror";
import LoadingSpinner from "../../LoadingSpinner";
import TaskManagerEditForm from "@/components/widgets/dataset/TaskManagerEditForm";
export default {
  name: "DatasetRowWidget",
  components: {
    DatePickerWidget,
    DeletePrompt,
    Popup,
    Icon,
    Button,
    InfoPanel,
    vmSelector,
    Codemirror,
    LoadingSpinner,
    TaskManagerEditForm
  },
  mixins: [
    DateMixin,
  ],
  props: {
    dataRow: {type: Object || Array, default: null},
    fields: {type: Object || Array, default: null},
    hiddenFields: {type: Array, required: true},
    datasetId: {type: String, required: true,},
    type: {type: String, required: true},
    deletable: {type: Boolean, default: false},
  },
  data() {
    return {
      row: {},
      selectedFile: "",
      fileContent: "",
      fileLoading: false,
      files: [],
      itemName: '',
      SpecialUuids: SpecialUuids,
      currentlyEditingRow: {
        id: '',
        index: '',
        value: '',
      },
      currentlyDeletingRow: '',
      showDeletePrompt: false,
      showEditForm: false,
      organizationId: '',
      TaskManagerTaskTypes: TaskManagerTaskTypes,
      TaskManagerStates: TaskManagerStates,
      resuming: false,
      waitingForResume: null,
      forceResume: false,
      fetching: false,
      abortFetch: false,
      error: "",
      assetId: ''
    };
  },
  computed: {
    TaskManagerTaskColors() {
      return TaskManagerTaskColors
    },
    getIndexType: function () {
      return 'columnIndex';
    },
  },
  watch: {
    dataRow: {
      handler() {
        this.setData();
      },
      deep: true
    }
  },
  beforeMount() {
    this.setData();
  },
  methods: {
    getColor(prio) {
      if (prio < 100) {
        return this.TaskManagerTaskColors[1];
      } else {
        return this.TaskManagerTaskColors[prio];
      }
    },
    getState(prio) {
      if(prio < 100) {
        return 'Running';
      } else {
       return this.TaskManagerStates[prio]
      }
    },
    deleteTask() {
      this.showDeletePrompt = true;
    },
    loadFile() {
      this.fileLoading = true;
      this.$store.dispatch('clientGetPipelineLogFile', {
        id: this.row.data.id,
        item: this.row.data.targetType.toLowerCase(),
        key: this.selectedFile
      }).then(content => {
        this.fileContent = content.text;
        this.fileLoading = false
      }).catch(e => {
        console.log(e);
        this.fileLoading = false;
      })
    },
    listFiles() {
      this.fileLoading = true;
      this.$store.dispatch('clientListPipelineLogFiles', {
        id: this.row.data.id,
        item: this.row.data.targetType.toLowerCase()
      }).then(files => {
        this.files = files.logFiles;
        this.fileLoading = false;
      }).catch(e => {
        console.log(e);
        this.fileLoading = false;
      })
    },
    isOverDue(heartBeat) {
      if (!heartBeat) {
        return false;
      }
      const pt = this.timeDifferenceFromTwoISOs(Date.now(), heartBeat);
      if (pt && pt.minutes > 15) {
        return true;
      }
      return false;
    },
    async getRow() {
      return await this.$store.dispatch('clientLoadDatasetRows', {
        id: this.datasetId,
        filter: `id eq ${this.dataRow.id}`
      });
    },
    async setData() {
      const localDataRow = await this.getRow();
      if (!localDataRow || !localDataRow[0]) {
        this.row = {};
        return;
      }
      this.row = JSON.parse(JSON.stringify(localDataRow[0]));
      const keys = Object.keys(this.fields);
      keys.map(item => {
        if (this.row.data[this.fields[item].columnIndex]) {
          const data = this.row.data[this.fields[item].columnIndex];
          delete this.row.data[this.fields[item].columnIndex];
          this.$set(this.row.data, item, data);
        } else {
          this.$set(this.row.data, item, '');
        }
      });
      if (!this.row.data) {
        this.row.data = {};
      }
      const {data} = this.row;
      if (!data) {
        return;
      }
      const {id, targetType, heartBeat} = data;
      if (id) {
        await this.loadAdditionalData(targetType, id);

      }
      this.checkOverdue(heartBeat)
    },
    async loadAdditionalData(targetType, id) {
      if (targetType === 'Asset') {
        this.$store.dispatch('clientLoadAsset', {id})
            .then(data => {
              this.itemName = data.name;
              this.organizationId = data.organizationId;
              this.assetId = id;
            })
      } else {
        this.$store.dispatch('clientGetCrossProjectInstances', {
          filter: `id eq ${id}`,
          include: ['asset', 'project']
        }).then(data => {
          if (data[0]) {
            this.itemName = data[0].name;
            this.organizationId = data[0].asset.organizationId;
            return this.$store.dispatch('clientLoadAssets', {
              filter: 'sourceProjectId eq ' + data[0].projectId
            })
          }
        }).then(assemblies => {
          if(assemblies.length) {
            this.assetId = assemblies[0].id;
          }
        })
      }
    },
    checkOverdue(heartBeat) {
      if (this.waitingForResume && heartBeat) {
        if (!this.isOverDue(heartBeat)) {
          this.waitingForResume = null;
        } else {
          const pt = this.timeDifferenceFromTwoISOs(Date.now(), this.waitingForResume);
          if (pt.minutes > 3) {
            this.waitingForResume = false;
            this.error = "seems like resuming didn't work as expected – please make sure, priority is below 100 and try again";
          }
        }
      }
    },
    startTask() {
      const {data} = this.row;
      if (!data) {
        return;
      }
      const {prio} = data;
      if (prio == 666) {
        this.error = 'you cannot resume a task with prio 666, set it back to prio 4 and then try again'
        return;
      }
      this.row.data.status = 'new';
      this.saveTask(this.row);
      setTimeout(() => {
      }, 2000);
      let dispatchMethod = 'clientAssetToPipeline'
      if (this.row.data.targetType === 'Instance') {
        dispatchMethod = 'clientInstanceToPipeline';
      }
      this.$store.dispatch(dispatchMethod, {
        id: this.row.key,
        type: this.row.data.task,
        virtualMachine: this.row.data.virtualMachine
      });
    },
    async finishTask(resume = false) {
      this.error = "";
      let payload = {
        id: this.row.key
      };
      if (resume) {
        payload.resume = true;
      }
      await this.$store.dispatch('clientFinishPipelineTask', payload)
          .then(() => {
            this.resuming = false;
            this.waitingForResume = Date.now();
          }).catch(e => {
            this.error = e;
          });
    },
    async resume() {
      this.error = "";
      // leave this on ==, because it might be a number or a string!
      if (this.row.data.prio == 666) {
        this.row.data.status = 4;
        await this.saveTask(this.row, false);
        //this.error = 'you cannot resume a task with prio 666, set it back to prio 4 and then try again'
        //return;
      }
      this.resuming = true;
      await this.finishTask(true);
    },
    async saveTask(row, triggerReload = true) {
      const {id, key, data} = row;
      const {targetType, task, userId, prio, status, virtualMachine} = data;
      const fields = {id: key, targetType, task, userId, prio, status, virtualMachine};
      return await this.$store.dispatch('clientUpdateDatasetRow', {
        id: this.datasetId,
        datarowId: id,
        key: key,
        listName: this.datasetId,
        data: fields
      }).then(data => {
        if (triggerReload) {
          this.$emit('updateDatasetRow', data);
        }
        this.showEditForm = false;
        return true;
      });
    },
    deleteDataRowFinally: function () {
      this.$store.dispatch('deleteDatasetRow', {
        id: this.datasetId,
        args: [this.dataRow.id],
        listName: this.datasetId,
      });
      this.$emit('updateDatasetRow', {id: this.dataRow.id, data: null})
      this.$store.dispatch('createNotification', {
        type: 'success',
        text: 'Task deleted – please check the item history and set the log entry to aborted if it is still marked as running'
      });
    },
    editDataRow: function (index) {
      this.currentlyEditingRow.index = index;
      if (this.dataRow.data[index]) {
        this.currentlyEditingRow.value = this.dataRow.data[index];
      }
    },
    focusDataField: function () {
      this.$refs.entry[0].$el.children[1].focus();
    },
    finishEditingDataRow: function () {
      this.currentlyEditingRow.index = '';
      this.currentlyEditingRow.value = '';
    },
    updateDataRow: function (val) {
      let value = val ? val : val.value;
      this.currentlyEditingRow.value = value;
    },
    saveDataRow: function () {
      let dataRowID = this.dataRow.id;
      //let value = val ? val : val.value;
      let value = this.currentlyEditingRow.value;
      let index = this.currentlyEditingRow.index;

      //make a deep copy
      let temp = JSON.parse(JSON.stringify(this.dataRow));
      if (!temp) {
        temp = this.dataRow.filter(item => {
          return item.id === this.dataRow.id
        })[0];
      }
      let dataRow = temp.data;
      dataRow[index] = value;

      let keys = Object.keys(this.fields);
      let fieldName = '';
      for (let i = 0; i < keys.length; i++) {
        if (this.fields[keys[i]].columnIndex === index) {
          fieldName = keys[i];
          break;
        }
      }
      if (!fieldName) {
        fieldName = index;
      }
      //find the fieldname end

      let params = {
        id: this.datasetId,
        data: {[fieldName]: value}, //replace by complete datarow
        datarowId: dataRowID,
        listName: this.datasetId,
      };
      this.$store.dispatch('clientUpdateDatasetRow', params).then(data => {
        this.$emit('updateDatasetRow', data);
      });
      this.$emit('dataRowChange', dataRowID, index, value);
      this.finishEditingDataRow();
      this.setData();
    },
  }
}
</script>

<style lang="scss">
.taskfield {
  display: inline-block;
  min-width: 150px;
}

.force-resume {
  position: absolute;
  bottom: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  transition: all 300ms ease;

  &:hover {
    background-color: $highlight;
  }
}

//transition
.toggle-enter-active, .toggle-leave-active {
  transition: all .3s ease-out;
  height: 80px;
  overflow: visible;
}

.toggle-enter, .toggle-leave-to {
  height: 0;
  overflow: hidden;
}

.data-row-delete {
  cursor: pointer;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

.table-filler {
  -webkit-transition: all 300ms ease;
  transition: all 300ms ease;
  position: relative;

  .icon {
    -webkit-transition: all 300ms ease;
    transition: all 300ms ease;
    opacity: 0;
    position: absolute;
    right: 6px;
    top: 50%;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    font-size: 0.8em;
  }

  &:hover {
    .icon {
      opacity: 1;
    }
  }
}
</style>